import Rellax from 'rellax';
import CrossIcon from '../../../svg/cross.svg';
import DashIcon from '../../../svg/dash.svg';
import DashReverseIcon from '../../../svg/dash-reverse.svg';
import PlusIcon from '../../../svg/plus.svg';
import RectangleIcon from '../../../svg/rectangle.svg';
import CircleIcon from '../../../svg/circle.svg';
import MastheadIcon from '../../../svg/masthead.svg';
import FaqsIcon from '../../../svg/faqs.svg';

/**
 * Class definition for `wp-shape` element
 *
 * @class WPShape
 * @extends {HTMLElement}
 */
class WPShape extends HTMLElement {
  constructor(self) {
    self = super(self);
    this.speedLimit = 1.5;
    return self;
  }

  /**
   * Tag name of element
   *
   * @readonly
   * @static
   */
  static get is() {
    return 'wp-shape';
  }

  static get icons() {
    return ['cross', 'dash', 'plus', 'rectangle', 'circle'];
  }

  static get shapes() {
    return [...document.querySelectorAll('wp-shape')];
  }

  static get pageShapes() {
    return [...document.querySelectorAll('wp-shape[page]')];
  }

  get parallax() {
    return this.hasAttribute('parallax');
  }

  get rotate() {
    return this.hasAttribute('rotate');
  }

  /**
   * In-page shapes are denoted by the page attribute
   *
   * @readonly
   * @memberof WPShape
   */
  get page() {
    return this.hasAttribute('page');
  }

  get speed() {
    if (this.hasAttribute('speed')) {
      return this.getAttribute('speed');
    }

    return false;
  }

  /**
   * Should be one of WPShape.icons
   */
  get shape() {
    if (this.hasAttribute('shape')) {
      return this.getAttribute('shape');
    }

    return false;
  }

  /**
   * Should be accent1 - accent6
   */
  get colour() {
    if (this.hasAttribute('colour')) {
      return this.getAttribute('colour');
    }

    return false;
  }

  get _icon() {
    return WPShape.icons[Math.floor(Math.random() * WPShape.icons.length)];
  }

  get _randomIndex() {
    return Math.floor(Math.random() * WPShape.icons.length);
  }

  get _speed() {
    if (this.speed) {
      return this.speed;
    } else {
      let speed = Math.floor(Math.random() * this.speedLimit) + 1;
      speed *= Math.floor(Math.random() * 2) === 1 ? 1 : -1;

      return speed;
    }
  }

  get _colour() {
    const colours = [
      'accent2',
      'accent3',
      'accent4',
      'accent5',
      'accent6',
      'accent8',
      'accent9',
    ];

    if (this.colour === '') return '';
    if (this.colour) return `c-shape--${this.colour}`;

    return 'c-shape--' + colours[Math.floor(Math.random() * colours.length)];
  }

  get _template() {
    // Pick random shape if none defined
    const shape = this.shape ? this.shape : this._icon;
    let shapeSvg;

    // Fetch the SVG source code to inject
    switch (shape) {
      case 'cross':
        shapeSvg = CrossIcon;
        break;
      case 'dash':
        shapeSvg = DashIcon;
        break;
      case 'dash-reverse':
        shapeSvg = DashReverseIcon;
        break;
      case 'plus':
        shapeSvg = PlusIcon;
        break;
      case 'rectangle':
        shapeSvg = RectangleIcon;
        break;
      case 'masthead':
        shapeSvg = MastheadIcon;
        break;
      case 'faqs':
        shapeSvg = FaqsIcon;
        break;
      default:
        shapeSvg = CircleIcon;
        break;
    }

    return `
      <i class="icon-${shape}">
        ${shapeSvg}
      </i>
    `;
  }

  get _pageHeight() {
    return document.body.clientHeight;
  }

  connectedCallback() {
    const classes = [`c-shape`];

    if (this._colour) {
      classes.push(this._colour);
    }

    this.classList.add(...classes);
    this.innerHTML = this._template;
    this.icon = this.querySelector('i');
    this.handleInPageShapes();
    this.handleParallax();
  }

  /**
   * Position in-page shapes
   *
   * @memberof WPShape
   */
  handleInPageShapes() {
    const minSpacing = 700;
    const numShapes = Math.floor(this._pageHeight / minSpacing);

    if (WPShape.pageShapes.length && WPShape.pageShapes.indexOf(this) >= 0) {
      for (let i = 0; i < WPShape.pageShapes.length; i++) {
        if (i < numShapes) {
          WPShape.pageShapes[i].style.top = minSpacing * (i + 1) + 'px';
          WPShape.pageShapes[i].classList.add(
            i % 2 === 0 ? 'c-shape--odd' : 'c-shape--even',
          );

          // Show any required shapes
          WPShape.pageShapes[i].classList.remove('u-is-hidden');
        }
      }
    }
  }

  /**
   * Parallax shapes
   *
   * @memberof WPShape
   */
  handleParallax() {
    if (this.parallax) {
      const rellax = new Rellax(this, {
        speed: this._speed,
        center: false,
        wrapper: null,
        round: true,
        vertical: true,
        horizontal: false,
        callback: ({ x, y }) => {
          if (!this.rotate) return false;
          const rotation = Math.ceil(
            (this.getBoundingClientRect().y / this._pageHeight) * 100,
          );

          this.icon.style.transform = `rotate(${rotation}deg)`;
        },
      });
    }
  }
}

customElements.define(WPShape.is, WPShape);
